import { addClass } from '@opten/dom/add-class';
import { PositionStrategy } from './position-strategy';
import { Overlay } from '../overlay';

/**
 * A strategy for positioning overlays. Using this strategy, an overlay is given an
 * explicit position relative to the browser's viewport. We use flexbox, instead of
 * transforms, in order to avoid issues with subpixel rendering which can cause the
 * element to become blurry.
 */
export class GlobalPositionStrategy implements PositionStrategy {
	/** The overlay to which this strategy is attached. */
	private overlayRef: Overlay;

	private cssPosition = 'static';
	private topOffset = '';
	private bottomOffset = '';
	private leftOffset = '';
	private rightOffset = '';
	private alignItems = '';
	private justifyContent = '';
	private width = '';
	private height = '';

	/** A lazily-created wrapper for the overlay element that is used as a flex container. */
	private wrapper: HTMLElement | null = null;

	constructor(private document: Document) {}

	attach(overlayRef: Overlay): void {
		const options = overlayRef.getOptions();

		this.overlayRef = overlayRef;

		if (this.width && !options.width) {
			overlayRef.updateSize({ width: this.width });
		}

		if (this.height && !options.height) {
			overlayRef.updateSize({ height: this.height });
		}
	}

	/**
	 * Sets the top position of the overlay. Clears any previously set vertical position.
	 * @param value New top offset.
	 */
	top(value = ''): this {
		this.bottomOffset = '';
		this.topOffset = value;
		this.alignItems = 'flex-start';
		return this;
	}

	/**
	 * Sets the left position of the overlay. Clears any previously set horizontal position.
	 * @param value New left offset.
	 */
	left(value = ''): this {
		this.rightOffset = '';
		this.leftOffset = value;
		this.justifyContent = 'flex-start';
		return this;
	}

	/**
	 * Sets the bottom position of the overlay. Clears any previously set vertical position.
	 * @param value New bottom offset.
	 */
	bottom(value = ''): this {
		this.topOffset = '';
		this.bottomOffset = value;
		this.alignItems = 'flex-end';
		return this;
	}

	/**
	 * Sets the right position of the overlay. Clears any previously set horizontal position.
	 * @param value New right offset.
	 */
	right(value = ''): this {
		this.leftOffset = '';
		this.rightOffset = value;
		this.justifyContent = 'flex-end';
		return this;
	}

	/**
	 * Centers the overlay horizontally with an optional offset.
	 * Clears any previously set horizontal position.
	 *
	 * @param offset Overlay offset from the horizontal center.
	 */
	centerHorizontally(offset = ''): this {
		this.left(offset);
		this.justifyContent = 'center';
		return this;
	}

	/**
	 * Centers the overlay vertically with an optional offset.
	 * Clears any previously set vertical position.
	 *
	 * @param offset Overlay offset from the vertical center.
	 */
	centerVertically(offset = ''): this {
		this.top(offset);
		this.alignItems = 'center';
		return this;
	}

	/**
	 * Apply the position to the element.
	 * @docs-private
	 *
	 * @returns Resolved when the styles have been applied.
	 */
	apply() {
		// Since the overlay ref applies the strategy asynchronously, it could
		// have been disposed before it ends up being applied. If that is the
		// case, we shouldn't do anything.
		if (!this.overlayRef.hasAttached()) {
			return;
		}

		const element = this.overlayRef.overlayElement;

		if (!this.wrapper && element.parentNode) {
			this.wrapper = this.document.createElement('div');
			addClass(this.wrapper, 'c-overlay__global'); // todo: settings?
			element.parentNode.insertBefore(this.wrapper!, element);
			this.wrapper!.appendChild(element);
		}

		const parentStyles = (element.parentNode as HTMLElement).style;
		const options = this.overlayRef.getOptions();

		element.style.position = this.cssPosition;
		element.style.marginLeft = options.width === '100%' ? '0' : this.leftOffset;
		element.style.marginTop = options.height === '100%' ? '0' : this.topOffset;
		element.style.marginBottom = this.bottomOffset;
		element.style.marginRight = this.rightOffset;

		parentStyles.justifyContent =
			options.width === '100%' ? 'flex-start' : this.justifyContent;
		parentStyles.alignItems =
			options.height === '100%' ? 'flex-start' : this.alignItems;
	}

	/** Removes the wrapper element from the DOM. */
	dispose() {
		if (this.wrapper && this.wrapper.parentNode) {
			this.wrapper.parentNode.removeChild(this.wrapper);
			this.wrapper = null;
		}
	}
}
